import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { IMeasurement } from '../../measurement/measurement.interface';
import { MeasurementService } from '../../measurement/measurement.service';
import { ISample } from '../../samples/interface/sample.interface';
import { ITaxon } from '../taxon.interface';
import { TaxonService } from '../taxon.service';
import { TaxonAbundanceCategoryService } from '../../taxon-abundance-category/services/taxon-abundance-category.service';
import { ABUNDANCE_CATEGORIES } from '@cpr/shared/abundance/abundance-category';

type PickMeasurement = Pick<IMeasurement, '_id' | 'analysedCount' | 'taxonAbundanceCategoryId'>;
type PickTaxon = Pick<ITaxon, 'taxonName' | 'cprTaxonId' | 'countingMethod'> & {
  taxonChildren?: PickTaxon[];
} & {
  measurement?: PickMeasurement;
};

@Component({
  selector: 'app-taxon-hierarchy-item',
  templateUrl: './taxon-hierarchy-item.component.html',
  styleUrls: ['./taxon-hierarchy-item.component.scss'],
})
export class TaxonHierarchyItemComponent implements OnChanges {
  @Input() taxon: PickTaxon;
  @Input() sample: ISample;

  @Input() showCategory: boolean;
  @Input() showDelete: boolean;
  @Input() showID = true;
  @Input() showOther: boolean;
  @Input() showChildren = true;

  @Output() measurementDelete = new EventEmitter<string>();

  category: number;

  fakeOtherTaxon: PickTaxon;

  get hasMissingCategoryWarning() {
    return (
      !!this.taxon?.measurement && !this.showOther && !this.taxon.measurement.taxonAbundanceCategoryId
    );
  }

  constructor(
    private readonly measurementService: MeasurementService,
    readonly taxonService: TaxonService, // Used in template
    private readonly taxonAbundanceCategoryService: TaxonAbundanceCategoryService,
  ) {}

  async ngOnChanges() {
    if (this.taxon.taxonChildren && typeof this.taxon?.measurement?.analysedCount === 'number') {
      this.fakeOtherTaxon = {
        taxonName: this.taxon.taxonName,
        cprTaxonId: this.taxon.cprTaxonId,
        countingMethod: this.taxon.countingMethod,
      };

      const countToNum = (count: PickMeasurement['analysedCount']) => {
        return typeof count === 'number' ? count : 0;
      };

      if (typeof this.taxon.measurement?.analysedCount === 'number') {
        this.fakeOtherTaxon.measurement = {
          analysedCount:
            countToNum(this.taxon.measurement.analysedCount) -
            this.taxon.taxonChildren.reduce(
              (sum, next) => sum + countToNum(next.measurement.analysedCount),
              0,
            ),
        };
      }
    }
    this.category = undefined;

    if (this.showOther) {
      try {
        this.category = await this.taxonAbundanceCategoryService.evaluateCategory(
          this.taxon?.measurement?.analysedCount,
          this.taxon.countingMethod,
          this.sample.factor,
        );
      } catch {
        this.category = undefined;
      }

      return;
    }

    if (!this.showCategory || !this.taxon?.measurement?.taxonAbundanceCategoryId) {
      return;
    }

    try {
      this.category = (
        await this.taxonAbundanceCategoryService.getCategoryById(
          this.taxon.measurement.taxonAbundanceCategoryId,
        )
      )?.taxonCategory;
    } catch {
      this.category = undefined;
    }
  }

  deleteMeasurement() {
    this.measurementService.deleteMeasurement(this.taxon.measurement._id, () => {
      this.measurementDelete.emit(this.taxon.measurement._id);
    });
  }
}
